import { inject, Injectable } from '@angular/core'

import { Resource, Role } from '@ti/contracts'
import { BaseCachedStore } from '@ti/core/models/cached-store'

import { RolesManager } from './roles.manager'

@Injectable({ providedIn: 'root' })
export class RoleStore extends BaseCachedStore<Role> {
  protected readonly CACHE_KEY = 'TI_ROLES'
  protected readonly CHECK_RESOURCE_ACCESS = Resource.Role
  protected readonly MAX_ITEMS = 1000

  protected readonly rolesManager = inject(RolesManager)

  protected override async init() {
    await super.init()

    // Refresh store automatically when roles are changed
    this.rolesManager.events$.updated.subscribe((ids) => {
      return ids?.length ? this.refreshById(ids) : this.refreshAll()
    })
  }

  protected override loadAll() {
    return this.api.role.list({ pageSize: this.MAX_ITEMS })
  }

  protected override loadById(ids: string[]) {
    return this.api.role.list({
      filter: { id: { in: ids } },
      pageSize: this.MAX_ITEMS,
    })
  }

  protected override loadSnapshot() {
    return this.api.role.list({
      select: ['id', 'updatedAt'],
      pageSize: this.MAX_ITEMS,
    })
  }
}

@Injectable()
export class TspFleetRoleStore extends BaseCachedStore<Role> {
  protected readonly CACHE_KEY = () => `TI_ROLES_FLEET_${this.fleetId}`
  protected readonly CHECK_RESOURCE_ACCESS = Resource.Fleet
  protected readonly MAX_ITEMS = 1000

  protected fleetId: number | undefined

  public setFleetId(id: number) {
    this.fleetId = id
    this.refreshAll().catch((e) => console.warn(`Cannot refresh`, e))
  }

  protected override loadAll() {
    if (!this.fleetId) {
      return Promise.resolve([])
    }
    return this.api.tsp.fleet.listRoles(this.fleetId, { pageSize: this.MAX_ITEMS })
  }

  protected override loadById(ids: string[]) {
    if (!this.fleetId) {
      return Promise.resolve([])
    }
    return this.api.tsp.fleet.listRoles(this.fleetId, {
      filter: { id: { in: ids } },
      pageSize: this.MAX_ITEMS,
    })
  }

  protected override loadSnapshot() {
    if (!this.fleetId) {
      return Promise.resolve([])
    }
    return this.api.tsp.fleet.listRoles(this.fleetId, {
      select: ['id', 'updatedAt'],
      pageSize: this.MAX_ITEMS,
    })
  }
}
